import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Mail, MessageSquare, Send, Copy, Check, ChevronDown } from 'lucide-react';

/**
 * Renders all communication scripts for the current appraisal status and
 * lets the user pick a recipient (client / tenant / agent), see the
 * rendered preview, copy to clipboard, or fire the "Send" action.
 *
 * Sending is logged server-side; actual SMS/email dispatch is currently
 * stubbed (mode='draft' returns the preview; mode='send' logs and tags
 * the entry as `pending_integration`).
 */
export default function SendScriptModal({ open, onClose, appraisalId, status, property = {}, agentLinked = false }) {
  const { api } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [activeRecipient, setActiveRecipient] = useState('client');
  const [activeScriptId, setActiveScriptId] = useState('');
  const [preview, setPreview] = useState(null);
  const [channel, setChannel] = useState('text');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState('');

  const isRental = !!property?.is_rental;
  const hasTenant = isRental && !!(property?.tenant_contact_name);
  const hasAgent = agentLinked || !!(property?.agent_name || property?.agent_email);

  const recipients = [
    { key: 'client', label: 'Client', available: true },
    { key: 'tenant', label: 'Tenant', available: hasTenant, hint: !hasTenant ? 'No tenant details' : '' },
    { key: 'agent', label: 'Agent', available: hasAgent, hint: !hasAgent ? 'No agent linked' : '' },
  ];

  // Load scripts for the current appraisal status when modal opens.
  useEffect(() => {
    if (!open) return;
    setSendStatus(''); setCopied(false);
    api.get('/communication-scripts', { params: { status } })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setScripts(list);
        // Pick first script matching current recipient
        const first = list.find(s => s.recipient === activeRecipient) || list[0];
        if (first) setActiveScriptId(first._id);
      })
      .catch(() => setScripts([]));
    // Keep activeRecipient/Script consistent on each open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status, api]);

  // When recipient changes, pick first script for that recipient.
  useEffect(() => {
    if (!scripts.length) return;
    const first = scripts.find(s => s.recipient === activeRecipient);
    if (first) {
      setActiveScriptId(first._id);
      // Default channel to script's preferred channel
      setChannel(first.channel === 'email' ? 'email' : 'text');
    } else {
      setActiveScriptId('');
      setPreview(null);
    }
  }, [activeRecipient, scripts]);

  // Re-render preview whenever script + channel changes.
  const refreshPreview = useCallback(async () => {
    if (!activeScriptId) { setPreview(null); return; }
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/send-script`, {
        script_id: activeScriptId,
        recipient: activeRecipient,
        channel,
        mode: 'draft',
      });
      setPreview(data.preview);
    } catch (e) {
      setPreview({ subject: '', body: 'Failed to render preview.', to: '' });
    }
  }, [activeScriptId, activeRecipient, channel, appraisalId, api]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);

  const copy = async () => {
    if (!preview) return;
    const text = channel === 'email'
      ? `To: ${preview.to}\nSubject: ${preview.subject}\n\n${preview.body}`
      : `To: ${preview.to}\n\n${preview.body}`;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  };

  const send = async () => {
    if (!activeScriptId) return;
    setSending(true); setSendStatus('');
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/send-script`, {
        script_id: activeScriptId, recipient: activeRecipient, channel, mode: 'send',
      });
      if (data.log?.status === 'pending_integration') {
        setSendStatus('Draft logged. Actual SMS/Email dispatch requires Twilio + SendGrid keys (admin will wire later).');
      } else {
        setSendStatus('Message sent.');
      }
    } catch (e) {
      setSendStatus(e.response?.data?.detail || 'Send failed.');
    }
    setSending(false);
  };

  if (!open) return null;

  const recipientScripts = scripts.filter(s => s.recipient === activeRecipient);
  const activeScript = scripts.find(s => s._id === activeScriptId);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="send-script-modal" className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-base font-bold text-[#0F172A]">Send communication</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Status: <span className="font-semibold text-[#D35400]">{status || 'unknown'}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500"><X size={16} /></button>
        </div>

        {/* Recipient tabs */}
        <div className="px-5 pt-4 flex gap-1.5 border-b border-slate-100">
          {recipients.map(r => (
            <button
              key={r.key}
              data-testid={`send-recipient-${r.key}`}
              disabled={!r.available}
              onClick={() => setActiveRecipient(r.key)}
              title={r.hint}
              className={`px-3.5 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${activeRecipient === r.key ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500 hover:text-slate-700'} ${!r.available ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {r.label}
              {!r.available && <span className="ml-1 text-[10px] font-normal italic">({r.hint})</span>}
            </button>
          ))}
        </div>

        {/* Script selector + channel */}
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          {recipientScripts.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No scripts configured for {activeRecipient} at this status. Add one in Settings → Communication Scripts.</p>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Script</label>
                <div className="relative">
                  <select
                    data-testid="send-script-select"
                    value={activeScriptId}
                    onChange={e => setActiveScriptId(e.target.value)}
                    className="w-full appearance-none pl-3 pr-9 py-2 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:border-[#D35400] bg-white"
                  >
                    {recipientScripts.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Channel</label>
                <div className="flex gap-2">
                  {['text', 'email'].map(c => (
                    <button
                      key={c}
                      data-testid={`send-channel-${c}`}
                      type="button"
                      onClick={() => setChannel(c)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-colors ${channel === c ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white border-[#E2E2DF] text-slate-600 hover:border-[#D35400]'}`}
                    >
                      {c === 'text' ? <MessageSquare size={13} /> : <Mail size={13} />}
                      {c === 'text' ? 'Text / SMS' : 'Email'}
                    </button>
                  ))}
                </div>
                {activeScript && activeScript.channel !== 'both' && activeScript.channel !== channel && (
                  <p className="text-[11px] text-amber-600 mt-1 italic">This script was authored for "{activeScript.channel}".</p>
                )}
              </div>

              {/* Preview */}
              {preview && (
                <div className="bg-slate-50 border border-[#E2E2DF] rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Preview</span>
                    <span className="text-[10px] text-slate-500">To: <span className="font-mono-num text-slate-700">{preview.to || '(no contact on file)'}</span></span>
                  </div>
                  {channel === 'email' && preview.subject && (
                    <p className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2" data-testid="send-preview-subject">{preview.subject}</p>
                  )}
                  <pre data-testid="send-preview-body" className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{preview.body}</pre>
                </div>
              )}

              {sendStatus && (
                <div className={`text-xs px-3 py-2 rounded-md border ${sendStatus.includes('sent') ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-testid="send-status">
                  {sendStatus}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50/50">
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-white font-medium">Close</button>
          <button data-testid="send-script-copy" onClick={copy} disabled={!preview} className="flex items-center gap-1.5 px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-700 hover:bg-white font-semibold disabled:opacity-40">
            {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy draft'}
          </button>
          <button data-testid="send-script-send" onClick={send} disabled={sending || !activeScriptId} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] disabled:opacity-50">
            <Send size={14} />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
